import React, { useState, useEffect } from "react";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Eye,
  EyeOff,
  Sparkles,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import api from "../api/client";

function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState({});
  const [copied, setCopied] = useState({});
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/v1/api-keys/");
      setKeys(response.data || []);
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      setError("Please enter a key name");
      return;
    }

    try {
      setCreating(true);
      setError("");
      const response = await api.post("/api/v1/api-keys/", {
        name: newKeyName.trim(),
      });

      setNewlyCreatedKey(response.data);
      setShowNewKeyModal(true);
      setNewKeyName("");

      await fetchKeys();
    } catch (err) {
      console.error("Error creating API key:", err);
      setError("Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId, keyName) => {
    if (
      !window.confirm(
        `Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/v1/api-keys/${keyId}`);
      await fetchKeys();
    } catch (err) {
      console.error("Error revoking API key:", err);
      setError("Failed to revoke API key");
    }
  };

  const handleCopyKey = (key, keyId) => {
    navigator.clipboard.writeText(key);
    setCopied({ ...copied, [keyId]: true });
    setTimeout(() => {
      setCopied({ ...copied, [keyId]: false });
    }, 2000);
  };

  const toggleShowKey = (keyId) => {
    setShowKey({ ...showKey, [keyId]: !showKey[keyId] });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  // New Key Modal Component
  const NewKeyModal = () => {
    if (!showNewKeyModal || !newlyCreatedKey) return null;

    const fullKey = newlyCreatedKey.key;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl shadow-cyan-500/10">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-600/20 flex items-center justify-center mx-auto mb-3 border border-cyan-500/20">
              <Key className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white">API Key Created!</h3>
            <p className="text-gray-400 text-sm mt-1">
              Copy this key now. You won't be able to see it again!
            </p>
          </div>

          <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between gap-2">
              <code className="text-cyan-400 text-sm font-mono break-all flex-1">
                {fullKey}
              </code>
              <button
                onClick={() => handleCopyKey(fullKey, "new")}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
                title="Copy key"
              >
                {copied["new"] ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={() => {
              setShowNewKeyModal(false);
              setNewlyCreatedKey(null);
            }}
            className="w-full py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02]"
          >
            I've Copied It
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Key className="w-6 h-6 text-cyan-400" />
          <h2 className="text-2xl font-bold text-white">API Keys</h2>
          <Sparkles className="w-4 h-4 text-cyan-400 ml-auto" />
        </div>
        <p className="text-gray-400">
          Manage API keys for integrating your applications with FrankTech.
        </p>
      </div>

      {/* Create Key Form */}
      <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6 mb-6">
        <h3 className="text-white font-semibold mb-3">Create New API Key</h3>
        <form
          onSubmit={handleCreateKey}
          className="flex flex-col sm:flex-row gap-3"
        >
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Enter key name (e.g., Production Server)"
            className="flex-1 bg-gray-900/50 border border-gray-800/50 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-200"
          />
          <button
            type="submit"
            disabled={creating}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Generate Key
              </>
            )}
          </button>
        </form>
        {error && (
          <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
      </div>

      {/* Keys List */}
      <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800/50">
          <h3 className="text-white font-semibold">Your API Keys</h3>
          <p className="text-gray-400 text-sm">{keys.length} keys</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-gray-600" />
            </div>
            <p className="text-gray-400">No API keys created yet</p>
            <p className="text-gray-500 text-sm mt-1">
              Generate your first key to start integrating
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-800/50">
            {keys.map((key) => (
              <div
                key={key.id}
                className="px-6 py-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{key.name}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          key.is_active
                            ? "bg-green-500/20 text-green-400 border border-green-500/30"
                            : "bg-red-500/20 text-red-400 border border-red-500/30"
                        }`}
                      >
                        {key.is_active ? "Active" : "Revoked"}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-400">
                      <span>Project: {key.project_name}</span>
                      <span>Created: {formatDate(key.created_at)}</span>
                      <span>Last used: {formatDate(key.last_used)}</span>
                      <span>Errors: {key.error_count || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-gray-950/50 border border-gray-800/50 rounded-lg px-3 py-1.5">
                      <code className="text-xs text-gray-400 font-mono">
                        {showKey[key.id]
                          ? key.key
                          : "•".repeat(Math.min(key.key.length, 20))}
                      </code>
                    </div>

                    <button
                      onClick={() => toggleShowKey(key.id)}
                      className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                      title={showKey[key.id] ? "Hide key" : "Show key"}
                    >
                      {showKey[key.id] ? (
                        <EyeOff className="w-4 h-4 text-gray-400 hover:text-white" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400 hover:text-white" />
                      )}
                    </button>

                    <button
                      onClick={() => handleCopyKey(key.key, key.id)}
                      className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                      title="Copy key"
                    >
                      {copied[key.id] ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
                      )}
                    </button>

                    {key.is_active && (
                      <button
                        onClick={() => handleRevokeKey(key.id, key.name)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Revoke key"
                      >
                        <Trash2 className="w-4 h-4 text-red-400 hover:text-red-300" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
        <p className="text-sm text-gray-400">
          <span className="text-cyan-400 font-medium">💡 Tip:</span> Use API
          keys for server-side integrations. Keep them secure and never expose
          them in client-side code.
        </p>
      </div>

      <NewKeyModal />
    </div>
  );
}

export default ApiKeys;
